/* eslint-disable camelcase */
import { useContext } from "react";
import { Event, LocalBusiness, NewsArticle, Organization, WithContext } from "schema-dts";
import { PageRatingContext } from "../../context/PageRatingContext";
import { SiteSettingsContext } from "../../context/SiteSettingsContext";
import { CalendarEvent } from "../../models/calendar/CalendarEvent";
import { SanityGeoPoint } from "../../models/geopoint/SanityGeoPoint";
import { SanityImageSourceExt } from "../../models/shared/SanityImageSourceExt";
import { SiteSettings } from "../../models/shared/SiteSettings";
import { isNullOrWhiteSpace } from "../../shared/string";
import { validImage } from "../../utilities/image";
import imageBuilder from "../sanity/imageBuilder";
import { PageSeoType } from "./DocumentHead";

export type CommonJsonLdPageData = {
  heroImage?: SanityImageSourceExt;
};

export type ArticleJsonLdPageData = {
  publishDate: string;
  _updatedAt: string;
};

export type LocalBusinessJsonLdPageData = {
  location?: SanityGeoPoint;
  contact?: {
    address?: string;
    phoneNumber?: string;
  };
};

export type EventJsonLdPageData = CalendarEvent;

export type CommonJsonLdProps = {
  title?: string;
  description?: string;
};

export type JsonLdProps = CommonJsonLdProps &
  (
    | {
        pageSeoType: PageSeoType.Article;
        pageData: CommonJsonLdPageData & ArticleJsonLdPageData;
      }
    | {
        pageSeoType: PageSeoType.LocalBusiness;
        pageData: CommonJsonLdPageData & LocalBusinessJsonLdPageData;
      }
    | {
        pageSeoType: PageSeoType.Organization;
        pageData: CommonJsonLdPageData;
      }
    | {
        pageSeoType: PageSeoType.Event;
        pageData: CommonJsonLdPageData & EventJsonLdPageData;
      }
    | {
        pageSeoType: PageSeoType.None;
        pageData: CommonJsonLdPageData;
      }
  );

function getPublisherOrganizationJsonLd(siteSettings: SiteSettings): Organization {
  const jsonLd: Organization = {
    "@type": "Organization",
    name: !isNullOrWhiteSpace(siteSettings.title) ? siteSettings.title : "Anticimex"
    // TODO: Email, geo
  };

  if (validImage(siteSettings.logoImage)) {
    const logoImageUrl = imageBuilder(siteSettings.logoImage)
      .fit("scale")
      .auto("format")
      .url({ friendlyFilename: true });

    jsonLd.logo = {
      "@type": "ImageObject",
      url: logoImageUrl
    };
  }

  return jsonLd;
}

export function useJsonLd(
  props: JsonLdProps
): WithContext<Event | NewsArticle | Organization | LocalBusiness> | null {
  const siteSettings = useContext(SiteSettingsContext);
  const [{ pageRating }] = useContext(PageRatingContext);

  if (props.pageSeoType === PageSeoType.None) {
    return null;
  }

  const { siteContact } = siteSettings;

  let heroImageUrl: string | null = null;

  if (validImage(props.pageData.heroImage)) {
    heroImageUrl = imageBuilder(props.pageData.heroImage)
      .fit("scale")
      .auto("format")
      .url();
  }

  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";

  switch (props.pageSeoType) {
    case PageSeoType.Article:
      return {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "@id": url,
        name: props.title,
        "headline": props.title,
        ...(!isNullOrWhiteSpace(heroImageUrl) && { image: heroImageUrl! }),
        datePublished: props.pageData.publishDate,
        dateModified: props.pageData._updatedAt,
        publisher: getPublisherOrganizationJsonLd(siteSettings)
      };
    case PageSeoType.Organization:
      return {
        // The type OrganizationLeaf is unfortunately not exported from schema-dts
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(getPublisherOrganizationJsonLd(siteSettings) as any),
        "@context": "https://schema.org",
        "@id": url,
        ...(!isNullOrWhiteSpace(heroImageUrl) && { image: heroImageUrl! }),
        ...(siteContact && {
          ...(!isNullOrWhiteSpace(siteContact.phoneNumber) && {
            telephone: siteContact.phoneNumber
          }),
          ...(!isNullOrWhiteSpace(siteContact.visitorAddress) && {
            address: siteContact.visitorAddress
          })
        })
      };
    case PageSeoType.LocalBusiness:
      return {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": url,
        name: props.title,
        ...(!isNullOrWhiteSpace(props.description) && { description: props.description }),
        ...(!isNullOrWhiteSpace(heroImageUrl) && { image: heroImageUrl! }),
        ...(props.pageData.contact && {
          address: props.pageData.contact.address,
          telephone: props.pageData.contact.phoneNumber
        }),
        ...(pageRating.active && {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: pageRating.averageRating ?? 0,
            reviewCount: pageRating.numRatings,
            bestRating: 5,
            worstRating: 1
          },
          review: {
            "@type": "Review",
            reviewRating: {
              "@type": "Rating",
              ratingValue: pageRating.userRating ?? 0,
              bestRating: 5,
              worstRating: 1
            }
          }
        }),
        ...(props.pageData.location && {
          geo: {
            "@type": "GeoCoordinates",
            latitude: props.pageData.location.lat,
            longitude: props.pageData.location.lng
          }
        })
      };
    case PageSeoType.Event:
      return {
        "@context": "https://schema.org",
        "@type": "Event",
        "@id": url,
        name: props.title,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        startDate: props.pageData.startDate,
        endDate: props.pageData.endDate,
        description: props.description,
        ...(!isNullOrWhiteSpace(heroImageUrl) && { image: heroImageUrl! })
        // TODO: location, performer, organizer
      };
  }
}
